import React from "react"
import dayjs from "dayjs"
import utc from "dayjs/plugin/utc"
import { useShallow } from "zustand/react/shallow"
import calendar from "dayjs/plugin/calendar"
import { useChatbot } from "@xogene/xogene-chatbot"
import AiLogo from "assets/images/ai_sparkle.svg"

// components
import { Tooltip, Select } from "antd"
import ButtonSlider from "components/ButtonSlider/ButtonSlider"
import { default as RevertConfirmModal } from "components/ConfirmModal/ConfirmModal"
import DownloadPreviewModal from "../../../DownloadPreviewModal/DownloadPreviewModal"
import VersionSaveModal from "../../../VersionSaveModal/VersionSaveModal"
import ReadOnlyActions from "../ReadOnlyAction/ReadOnlyActions"

// apis
import { useJrctCsvDownload, useGetAuthoringStudy, useGetAuthoringVersions } from "api/hooks/studies/useAuthoringApi"
import { useUserSettings, useUserPermissions } from "api/hooks"
import { useGetEnvVars } from "api/hooks/enum"

// stores
import useAuthoringStore from "../../../../hooks/stores/useAuthoringStore"
import useAuthoringViewStore from "../../../../hooks/stores/useAuthoringViewStore"
import useAuthoringTranslations from "../../../../hooks/stores/useAuthoringTranslations"
import useAuthoringPrintStore from "../../../../hooks/stores/useAuthoringPrintStore"
import useAuthoringVersionStore from "../../../../hooks/stores/useAuthoringVersionStore"
import useAuthoringPrintActions from "../../../../hooks/actions/useAuthoringPrintActions"
import useAuthoringDataStore from "../../../../hooks/stores/useAuthoringDataStore"
import useAuthoringFieldStore from "../../../../hooks/stores/useAuthoringFieldStore"
import useAuthoringSaveActions from "../../../../hooks/actions/useAuthoringSaveActions"
import useAuthoringValidationActions from "../../../../hooks/actions/useAuthoringValidationActions"
import useAppState from "context/hooks/useAppState"

// utils/helpers
import { getNextMinorVersNumber, getNextMajorVersNumber, formatVersNumber } from "../../../../utils"
import fileDownload from "utilities/fileDownload"
import { REGISTRY_PREVIEW_TYPE } from "constants/authoring"
import { AUTHORING_TASKS } from "constants/tasks.js"

// constants
import { DEFAULT_DATE_FORMAT } from "constants/globals"

// styles
import "./RightActions.scss"

dayjs.extend(calendar)
dayjs.extend(utc)

export default function RightActions({ formDataRef }) {
  const { data: envVars } = useGetEnvVars()
  const { getJrctCsvDownload } = useJrctCsvDownload()
  const { taskType } = useAppState
  const { authoringId, setLockedFullName, isUserLocked, showSaveLock, lastSaved, enableAutoSave } = useAuthoringStore(
    useShallow((state) => ({
      authoringId: state.authoringId,
      setLockedFullName: state.setLockedFullName,
      isUserLocked: state.isUserLocked,
      showSaveLock: state.showSaveLock,
      lastSaved: state.lastSaved,
      enableAutoSave: state.enableAutoSave,
    }))
  )

  const { data: permissions } = useUserPermissions()
  const { data: userSettings } = useUserSettings()
  const { data: authoring } = useGetAuthoringStudy(authoringId)
  const { data: versions } = useGetAuthoringVersions(authoring?.id)

  // slider buttons open state
  const [formActionSliderOpen, setFormActionSliderOpen] = React.useState(false)
  const [versSliderOpen, setVersSliderOpen] = React.useState(false)
  const [saveVersSliderOpen, setSaveVersSliderOpen] = React.useState(false)

  const [selectedVersOption, setSelectedVersOption] = React.useState(null)
  const [versDropdownOptions, setVersDropdownOptions] = React.useState([])
  const [openRevertModal, setOpenRevertModal] = React.useState(false)
  const [openDownloadPreview, setOpenDownloadPreview] = React.useState(false)
  const [openVersionModal, setOpenVersionModal] = React.useState(false)
  const [saveVersType, setSaveVersType] = React.useState(null)

  const { setOpenChatbot } = useChatbot()
  const { setForkedFields } = useAuthoringFieldStore(
    useShallow((state) => ({
      setForkedFields: state.setForkedFields,
    }))
  )

  const { setFormData, setForkedStudyData, setOverrideData, setTranslationsData } = useAuthoringDataStore(
    useShallow((state) => ({
      setFormData: state.setFormData,
      setForkedStudyData: state.setForkedStudyData,
      setOverrideData: state.setOverrideData,
      setTranslationsData: state.setTranslationsData,
    }))
  )

  const { handleSubmit } = useAuthoringSaveActions()

  const {
    formView,
    setFormView,
    formSection,
    readOnly,
    setReadOnly,
    showVersionCompare,
    setShowVersionCompare,
    showPagePreview,
    setShowPagePreview,
    fullScreen,
    setFullScreen,
    showUniqueRegistryPreviewType,
    setShowUniqueRegistryPreviewType,
    showRegistryBadges,
    setShowRegistryBadges,
    isReviewerView,
    setLoadingForm,
  } = useAuthoringViewStore(useShallow((state) => state))

  const { setIsPrinting } = useAuthoringPrintStore(
    useShallow((state) => ({
      setIsPrinting: state.setIsPrinting,
    }))
  )

  const { isTranslationActive, translationLanguages, setIsTranslationActive, selectedLanguage } =
    useAuthoringTranslations(
      useShallow((state) => ({
        selectedLanguage: state.selectedLanguage,
        isTranslationActive: state.isTranslationActive,
        translationLanguages: state.translationLanguages,
        setIsTranslationActive: state.setIsTranslationActive,
      }))
    )

  const { selectedVersion, setSelectedVersion, versionData } = useAuthoringVersionStore(
    useShallow((state) => ({
      selectedVersion: state.selectedVersion,
      setSelectedVersion: state.setSelectedVersion,
      versionData: state.versionData,
    }))
  )

  const { onPrintPage } = useAuthoringPrintActions()

  const { onRegistryValidate } = useAuthoringValidationActions(authoringId)

  const onChangeFullScreen = (event) => {
    if (!document.fullscreenElement) {
      setFullScreen(false)
    }
  }

  React.useEffect(() => {
    document.addEventListener("fullscreenchange", onChangeFullScreen)

    return () => {
      setReadOnly(true)
      document.removeEventListener("fullscreenchange", onChangeFullScreen)
    }
  }, [])

  React.useEffect(() => {
    if (versions) {
      const options = versions?.map((ver) => {
        const versNum = formatVersNumber(ver.version_num)
        let versDate = ""
        if (userSettings?.date_format) {
          versDate = dayjs(ver.date_created).format(userSettings.date_format)
        } else {
          versDate = dayjs(ver.date_created).format(DEFAULT_DATE_FORMAT)
        }

        return {
          value: ver.version_num,
          label: `${versNum} - Created by ${ver.created_by_fullname} on ${versDate} (${ver.notes})`,
        }
      })
      setVersDropdownOptions(options)
    }
  }, [versions, userSettings])

  React.useEffect(() => {
    if (authoring?.locked_by_fullname) setLockedFullName(authoring.locked_by_fullname)
  }, [authoring?.locked_by_fullname])

  const lastSavedMessage = React.useMemo(() => {
    if (lastSaved) return `Last saved: ${lastSaved.calendar()}`
  }, [lastSaved])

  const menuItems = React.useMemo(() => {
    return {
      save: [
        {
          iconClassName: "fal fa-arrow-circle-down",
          label: `Save as version ${getNextMinorVersNumber(authoring?.version)}`,
          onClick: () => onOpenVersModal("minor"),
        },
        {
          iconClassName: "fal fa-arrow-circle-up",
          label: `Save as version ${getNextMajorVersNumber(authoring?.version)}`,
          onClick: () => onOpenVersModal("major"),
        },
      ],
      download: [
        {
          iconClassName: "fal fa-file-download",
          label: "Download",
          disabled: AUTHORING_TASKS.prsPreview === taskType,
          onClick: () => setOpenDownloadPreview(true),
        },
        {
          iconClassName: "fal fa-file-download",
          label: "Download CSV",
          onClick: () =>
            getJrctCsvDownload(authoringId, formSection?.id).then((res) => {
              fileDownload(
                res.csv_resp,
                `${authoring.study_id}_jRCT_CSV_(${dayjs().format(userSettings?.date_format)})`,
                res.content_type
              )
            }),
        },
        {
          iconClassName: "fal fa-file-invoice",
          label: formView?.id === "eudra" ? "EUDRACT Preview" : "Preview",
          onClick: () => {
            if (formView?.id === "global") {
              setFormView({ id: "ctg", text: "CTG" })
            }
            setShowUniqueRegistryPreviewType(null)
            setShowPagePreview(true)
          },
          disabled: formView?.id === "ctg" && AUTHORING_TASKS.prsPreview === taskType,
        },
        {
          iconClassName: "fal fa-file-invoice",
          label: "CTIS Preview",
          onClick: () => {
            setShowUniqueRegistryPreviewType(REGISTRY_PREVIEW_TYPE.ctisResults)
            setShowPagePreview(true)
          },
        },
        {
          iconClassName: "fal fa-print",
          label: "Print",
          onClick: () => onPrintPage({ showUniqueRegistryPreviewType }),
        },
      ],
      validate: {
        iconClassName: "fal fa-file-check",
      },
    }
  }, [
    versions,
    formView,
    authoring,
    showVersionCompare,
    isTranslationActive,
    formSection,
    showUniqueRegistryPreviewType,
    taskType,
  ])

  if (isUserLocked) {
    return <ReadOnlyActions authoringId={authoring.id} />
  }

  /** EVENT HANDLERS */
  const getVersionNumber = () => {
    if (selectedVersion?.version_num) {
      return selectedVersion.version_num.toFixed(1)
    }
    return null
  }

  const onVersionSelect = (value, option) => {
    setShowVersionCompare(true)
    setIsPrinting(true)
    setReadOnly(true)
    const currentVers = versions?.find((ver) => {
      return ver.version_num === value
    })

    setSelectedVersOption(option)
    setSelectedVersion(currentVers)
  }

  // when user clicks exit version compare button
  const onVerCompareExit = () => {
    setShowVersionCompare(false)
    setIsPrinting(false)
    setSelectedVersion(null)
    setSelectedVersOption(null)
  }

  const handleVersRevert = () => {
    setLoadingForm(true)
    onVerCompareExit()
    revertToVersion()
    setVersSliderOpen(false)
    setSaveVersSliderOpen(false)
    setTimeout(() => {
      setLoadingForm(false)
    }, 1000)
  }

  const revertToVersion = () => {
    if (versionData?.study_data) {
      setForkedFields(null)

      const formData = versionData.study_data
      const overrideData = versionData?.override_data || {}
      const forkedStudyData = formData?.forked_study_data || {}
      const translationsData = formData?.translations || {}
      formDataRef.current = formData
      setFormData(formData)
      setOverrideData(overrideData)
      setForkedStudyData(forkedStudyData)
      setTranslationsData(translationsData)
      handleSubmit({ unlockOnSave: true, formData, overrideData, forkedStudyData, translationsData })
    } else {
      console.log("ERROR: no version data selected.")
    }
  }

  const onCloseMenu = () => {
    setShowPagePreview(false)
    onVerCompareExit()
    setShowUniqueRegistryPreviewType(null)
  }

  const onOpenVersModal = (type) => {
    setSaveVersType(type)
    setOpenVersionModal(true)
    setOpenVersionModal(true)
    setFormActionSliderOpen(false)
  }

  const showValidate = () => {
    if (formView?.id) {
      return formView.id === "ctg" || formView.id === "eudra" || formView.id === "eupas" || formView.id === "jrct"
    }
    return false
  }

  const onEnterFullScreen = () => {
    requestFullscreen(document?.documentElement)
    setFullScreen(true)
  }

  const requestFullscreen = (element) => {
    if (element.requestFullscreen) {
      element.requestFullscreen()
    } else if (element.webkitRequestFullscreen) {
      element.webkitRequestFullscreen()
    } else if (element.mozRequestFullScreen) {
      element.mozRequestFullScreen()
    } else if (element.msRequestFullscreen) {
      element.msRequestFullscreen()
    }
  }

  return (
    <div className="right-container">
      <div className="right-actions-container">
        {lastSaved && fullScreen === false && <div className="authoring-hover-text">{lastSavedMessage}</div>}
        {envVars?.AI_ENABLED === "true" && (
          <div className={`icon-wrapper`}>
            <img
              src={AiLogo}
              className="ai-svg-icon dropdown-toggle"
              onClick={() => setOpenChatbot(true)}
            />
          </div>
        )}
        {permissions?.["study.edit"] && showSaveLock && enableAutoSave && (
          <Tooltip title={showSaveLock ? "Save and unlock" : "Save"}>
            <div
              onClick={() => {
                handleSubmit({ unlockOnSave: true })
                setReadOnly(true)
              }}
              className={`form-action-btn${showSaveLock ? " locked" : ""}`}
              style={{ marginBottom: 3 }}
            >
              <i className={showSaveLock ? "fal fa-lock-alt" : "far fa-unlock-alt"} />
            </div>
          </Tooltip>
        )}
        {permissions?.["study.edit"] && readOnly && !showVersionCompare && (
          <Tooltip title="Edit form">
            <div
              onClick={() => setReadOnly(false)}
              className={`form-action-btn`}
            >
              <i
                className={"fal fa-edit"}
                data-dd-action-name="Edit Form"
              />
            </div>
          </Tooltip>
        )}
        {formSection?.id !== "secondaryAttributes" &&
          formSection?.id !== "sponsorTrialWebsite" &&
          isReviewerView !== true && (
            <>
              {showRegistryBadges ? (
                <Tooltip title="Hide registry badges">
                  <i
                    className="fal fa-eye icon-button"
                    onClick={() => setShowRegistryBadges(false)}
                  />
                </Tooltip>
              ) : (
                <Tooltip title="Show registry badges">
                  <i
                    data-dd-action-name="Show registry badges"
                    className="fal fa-eye-slash icon-button"
                    onClick={() => setShowRegistryBadges(true)}
                  />
                </Tooltip>
              )}
            </>
          )}
        {/* Disabled when AUTHORING_TASKS.validation === taskType */}
        {showValidate() && showPagePreview !== true && (
          <Tooltip title="Validate">
            <div
              onClick={
                AUTHORING_TASKS.validation === taskType ? null : () => onRegistryValidate({ form_view: formView?.id })
              }
              className={`form-action-btn${AUTHORING_TASKS.validation === taskType ? " form-action-btn-disabled" : ""}`}
              disabled={AUTHORING_TASKS.validation === taskType}
            >
              <i
                className="fal fa-file-check disabled"
                style={{ marginBottom: 2 }}
                data-dd-action-name="Validate"
              />
            </div>
          </Tooltip>
        )}
        <ButtonSlider
          iconClassName={"fal fa-file-download btn-slider"}
          menuItems={menuItems.download.filter((item) => {
            if (item.label === "Download" && formView.id !== "ctg") return false
            else if (item.label === "Preview" && formView.id === "global") return false
            else if (item.label === "Download CSV" && formView.id !== "jrct") return false
            else if (item.label === "CTIS Preview" && formView.id !== "eudra") return false
            return true
          })}
          tooltip={"Form actions"}
          open={formActionSliderOpen}
          onChange={setFormActionSliderOpen}
          onOpenMenu={() => {
            setVersSliderOpen(false)
            setSaveVersSliderOpen(false)
          }}
          onCloseMenu={onCloseMenu}
          wrapClassName="form-action-btn-wrap icon-button"
        />
        <ButtonSlider
          iconClassName={"fal fa-game-board-alt btn-slider icon-button"}
          tooltip="Version compare"
          open={versSliderOpen}
          onChange={setVersSliderOpen}
          onOpenMenu={() => {
            setFormActionSliderOpen(false)
            setSaveVersSliderOpen(false)
          }}
          onCloseMenu={onCloseMenu}
          prefix={
            formView?.id === "eupas" && (
              <Tooltip title="When comparing the current form to a version of the form created prior to the EMA RWD Catalogue update, fields that existed in the older version of the form (EU PAS) will not be available for comparison.">
                <i className="fa-light fa-circle-info" />
              </Tooltip>
            )
          }
        >
          <>
            <Select
              placeholder="Compare to version..."
              options={versDropdownOptions}
              style={{ minWidth: 195 }}
              onChange={onVersionSelect}
            />
            {permissions?.["study.form.revert"] && selectedVersOption && (
              <Tooltip
                title={`Revert to version ${Number(selectedVersOption?.value).toFixed(1)}`}
                placement={"left"}
              >
                <i
                  className="fal fa-undo"
                  style={{ fontSize: 16 }}
                  onClick={() => setOpenRevertModal(true)}
                />
              </Tooltip>
            )}
          </>
        </ButtonSlider>
        {translationLanguages.length > 0 && (
          <Tooltip title="Toggle translations">
            <div
              onClick={() => setIsTranslationActive(!isTranslationActive)}
              className="form-action-btn language-btn"
            >
              <i
                className={`${isTranslationActive ? "fa-regular fa-language icon-btn-active" : "fa-light fa-language"}`}
              />
            </div>
          </Tooltip>
        )}
        {fullScreen === false && (
          <Tooltip title="Full screen">
            <div
              onClick={onEnterFullScreen}
              className={`form-action-btn`}
            >
              <i
                data-dd-action-name="Full Screen"
                className="fa-light fa-expand"
                style={{ marginBottom: 0, marginLeft: 4 }}
              />
            </div>
          </Tooltip>
        )}
        {permissions?.["study.edit"] && (
          <ButtonSlider
            iconClassName={"fal fa-save btn-slider"}
            type={"primary"}
            menuItems={menuItems.save}
            tooltip={"Save version"}
            open={saveVersSliderOpen}
            onChange={setSaveVersSliderOpen}
            disabled={!enableAutoSave}
            onOpenMenu={() => {
              setFormActionSliderOpen(false)
              setVersSliderOpen(false)
            }}
            onCloseMenu={onCloseMenu}
            wrapClassName="save-btn-wrap"
          />
        )}
      </div>
      <RevertConfirmModal
        openModal={openRevertModal}
        setOpenModal={setOpenRevertModal}
        header={`Are you sure you want to revert to version #: ${getVersionNumber()}`}
        content={
          <div>This action cannot be undone. All overrides made after the version was created will be lost.</div>
        }
        actionLabel="Revert"
        onAction={() => handleVersRevert()}
        actionVariant="primary"
      />
      <DownloadPreviewModal
        openDownloadPreview={openDownloadPreview}
        setOpenDownloadPreview={setOpenDownloadPreview}
        authoringId={authoring?.id}
      />
      <VersionSaveModal
        type={saveVersType}
        open={openVersionModal}
        onHide={() => {
          setOpenVersionModal(false)
          setFormActionSliderOpen(false)
          setSaveVersSliderOpen(false)
        }}
        onCloseMenu={onCloseMenu}
        authoring={authoring}
        onSubmit={handleSubmit}
      />
    </div>
  )
}
